import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

/**
 * The YouTube mark, in the two treatments its counter — the play triangle —
 * gets on a storefront.
 *
 * The body is one drawing whose `d` carries the triangle as a knocked-out
 * subpath, so the mark is a hole wherever nothing paints over it. `variant`
 * adds a COLOUR on top of that hole rather than a second copy of the artwork:
 * the counter path traces the same triangle and is either unpainted, leaving
 * the hole, or filled.
 *
 * The axis is real and `className` cannot reach it: painting a child element
 * from the call site is what the retired `childClassName` prop did, and the
 * bar `.docs/CONVENTIONS.md` sets for an icon carrying a `cva` at all is
 * exactly that — an appearance the caller cannot express through `className`.
 *
 * `variant` is required and the recipe declares no `defaultVariants`, because
 * an appearance decided by an omission is invisible in review.
 */
const iconYoutubeCounterVariants = cva('', {
  variants: {
    variant: {
      /** The triangle stays a hole: whatever sits behind the mark shows through. */
      knockout: 'fill-none',
      /** The triangle is painted, the way a play button over a still reads. */
      inverted: 'fill-foreground-inverted',
    },
  },
});

/**
 * The counter treatments, named for the look and never for whoever asked for
 * one. Derived from the recipe rather than written beside it, so an arm added
 * there cannot go unpublished here.
 *
 * `NonNullable` rather than `VariantProps` directly: that helper makes every
 * axis optional, which is exactly the omission this part refuses to allow.
 */
export type IconYoutubeVariant = NonNullable<
  VariantProps<typeof iconYoutubeCounterVariants>['variant']
>;

export const IconYoutube = ({
  variant,
  className,
  ...props
}: ComponentProps<'svg'> & { readonly variant: IconYoutubeVariant }) => (
  <svg
    data-slot="icon-youtube"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 65 64"
    fill="none"
    className={clsxm('h-16 w-[65px] shrink-0', className)}
    {...props}
  >
    <path
      d="M33.2159 50.869L22.1372 50.6632C18.5501 50.5915 14.9542 50.7346 11.4374 49.9917C6.08773 48.8817 5.70873 43.439 5.31216 38.8737C4.76572 32.4554 4.97726 25.9206 6.00847 19.5559C6.59062 15.9846 8.88161 13.8536 12.4246 13.6217C24.3846 12.7802 36.4242 12.8799 48.3577 13.2725C49.6181 13.3085 50.8871 13.5052 52.1298 13.7292C58.2643 14.8213 58.4139 20.989 58.8115 26.1811C59.2081 31.4267 59.0407 36.6993 58.2827 41.9092C57.6746 46.2229 56.5111 49.8403 51.6009 50.1895C45.4489 50.6462 39.438 51.0138 33.2686 50.8967C33.2689 50.869 33.2335 50.869 33.2159 50.869ZM26.7027 39.948C31.3388 37.2444 35.8864 34.5859 40.496 31.9004C35.8512 29.1968 31.3122 26.5383 26.7027 23.8528V39.948Z"
      fill="currentColor"
    />
    <path
      d="M26.7027 39.948C31.3388 37.2444 35.8864 34.5859 40.496 31.9004C35.8512 29.1968 31.3122 26.5383 26.7027 23.8528V39.948Z"
      className={iconYoutubeCounterVariants({ variant })}
    />
  </svg>
);
